import { AppPageLayout } from "@/components/common/AppPageLayout";
import { useScreenerCopy } from "@/features/i18n/screenerCopy";
import { publicFormRoutePaddingClass } from "@/features/pages/components/formPageLayout";
import { useSelectScreener } from "@/features/screeners/api/selectScreener";
import ScreenerResult from "@/features/screeners/components/ScreenerResult";
import ScreenerChecking from "@/features/screeners/components/ScreenerChecking";
import ScreenerUnavailable from "@/features/screeners/components/ScreenerUnavailable";
import {
    parseScreenerSearch,
    screenerSignupSearch,
    type ScreenerSearchT,
} from "@/features/screeners/utils/screenerSearch";
import { isUuid } from "@/services/isUuid";
import type { ScreenerOutcomeT } from "@patient-watch/types";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";

const ScreenerResultPage = () => {
    const { screenerId } = Route.useParams();
    const { outcome, signup, quickstart_id: savedQuickstartId } = Route.useSearch();
    const copy = useScreenerCopy();
    const { data: screener, isLoading } = useSelectScreener(screenerId);
    // Reuse the quickstart_id the answers were saved with, so they reach the diary sign-up creates.
    const signupSearch = useMemo(
        () => screenerSignupSearch(signup, savedQuickstartId ?? crypto.randomUUID()),
        [signup, savedQuickstartId]
    );

    if (isLoading) return <ScreenerChecking />;

    if (!screener) return <ScreenerUnavailable />;

    const isEligible = outcome === "eligible";
    const title = (isEligible ? screener.eligible_title : screener.ineligible_title)?.trim();
    const body = (isEligible ? screener.eligible_body : screener.ineligible_body)?.trim();

    return (
        <AppPageLayout width="md" padding="sm" className={publicFormRoutePaddingClass}>
            <ScreenerResult
                screenerId={screenerId}
                outcome={outcome}
                title={title || (isEligible ? copy.eligibleTitle : copy.ineligibleTitle)}
                body={body || (isEligible ? copy.eligibleBody : copy.ineligibleBody)}
                signupSearch={signupSearch}
                signup={signup}
            />
        </AppPageLayout>
    );
};

export const Route = createFileRoute("/screen/$screenerId/result")({
    component: ScreenerResultPage,
    validateSearch: (
        search: Record<string, unknown>
    ): ScreenerSearchT & { outcome: ScreenerOutcomeT; quickstart_id?: string } => ({
        ...parseScreenerSearch(search),
        outcome: search.outcome === "eligible" ? "eligible" : "ineligible",
        ...(isUuid(search.quickstart_id) ? { quickstart_id: search.quickstart_id as string } : {}),
    }),
});
